<template>
    <!-- *********************************************
	*                       Filters                	 *
	********************************************** -->
    <converter-filters
    :nonAuthenticatedFeature="nonAuthenticatedFeature"
    :exchangeRatesTableList="exchangeRatesTableList"
    :appliedFilters="appliedFilters"
    @applyFilters="emit('applyFilters', $event)" />
    <!-- *********************************************
	*                        Table                	 *
	********************************************** -->
    <transition name="common-enter-animation" mode="out-in" appear>
        <div v-if="!showConverterLoader" class="white-box-wrapper prevent-auto-margins" :class="{ 'mobile': getMobileAppState }">
            <!-- Last Update Date -->
            <div v-if="getMobileAppState" class="last-update-date">
                <icon-cycle/>{{ t('exchanges.table.lastUpdateDate', { date: lastUpdateDate }) }}
            </div>
            <!-- Mobile Tabs -->
            <tabs-segment v-if="getMobileAppState" :tabsList="tabsSegmentList" @clicked="tabsSegmentClickHandler"/>
            <div v-if="parsedTableList.length" class="converter-table" :class="{'non-authenticated-feature': nonAuthenticatedFeature}">
                <!-- Table -->
                <div class="table-container">
                    <!-- Table Headers -->
                    <div class="table-headers">
                        <div class="header currency-cell">{{ t('exchanges.table.tableHeaders.currency') }}</div>
                        <div v-if="!getMobileAppState" class="header">{{ t('exchanges.table.titles.divisas') }}</div>
                        <div v-if="!getMobileAppState" class="header">{{ t('exchanges.table.titles.notas') }}</div>
                    </div>
                    <!-- Table Row -->
                    <div v-for="(row, index) in parsedTableList" class="table-row" :key="index">
                        <div class="cell currency-cell">
                            <component class="currency-icon" :is="row.currencyIcon" />
                            <div class="description-container">
                                <span class="currency-text">{{ row.currency }}</span>
                                <span class="currency-description">{{ row.description }}</span>
                            </div>
                        </div>
                        <div v-if="showDivisas" class="cell">
                            <span class="converted-value">{{ row.divisas.convertedSellValue }}</span>
                            <span class="description">
                                {{ selectedCurrency === 'AOA' ? `1 AOA = ${row.divisas.buyValue}` : `1 ${selectedCurrency} = ${row.divisas.sellValue}` }}
                            </span>
                        </div>
                        <div v-if="showNotas" class="cell">
                            <span class="converted-value">{{ row.notas.convertedSellValue }}</span>
                            <span class="description">
                                {{ selectedCurrency === 'AOA' ? `1 AOA = ${row.notas.buyValue}` : `1 ${selectedCurrency} = ${row.notas.sellValue}` }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Empty State -->
            <empty-state-generic
            v-else
            illustration="emptyState/converter"
            :title="t('exchanges.converter.emptyState.title')"
            :description="t('exchanges.converter.emptyState.description')"/>
        </div>
    </transition>
</template>

<script setup>
import ConverterFilters from '@/components/features/exchanges/ConverterFilters'
import IconCycle from '@/assets/icons/cycle.svg?inline'
import TabsSegment from '@/components/generic/tabs/Segment'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'
/* Aux */
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits(['applyFilters'])

/** Props
@nonAuthenticatedFeature {Boolean} to apply specific styles for nonAuthenticatedFeature
@converterTableList {array} - list for table
@appliedFilters {Object} - filter
@showConverterLoader {Boolean} prop to show hide table
@lastUpdateDate {string} - exchange rates last update date
*/
const props = defineProps({
    nonAuthenticatedFeature: Boolean,
    exchangeRatesTableList: Array,
    converterTableList: Array,
    appliedFilters: Object,
    showConverterLoader: Boolean,
    lastUpdateDate: String
})

/* mapGetters */
const { getMobileAppState } = mapGetters()

const selectedCurrency = computed(() => {
    return props.appliedFilters.currency
})

const parsedTableList = computed(() => {
    return parsedTableListArrayGroup.value.map((obj, index) => ({
        currency: obj[1].currency === 'AKZ' ? 'AOA' : obj[1].currency,
        description: obj[1].description,
        currencyIcon: defineAsyncComponent(() => import('@/assets/icons/flags/' + flagCurrenciesIconMapper(obj[1].currency) + '.svg?inline')),
        divisas: {
            sellValue: obj[1].sellValue.toFixed(6).replace('.', ','),
            convertedSellValue: obj[1].convertedSellValue.toFixed(6).replace('.', ','),
            buyValue: obj[1].buyValue.toFixed(6).replace('.', ','),
            convertedBuyValue: obj[1].convertedBuyValue.toFixed(6).replace('.', ',')
        },
        notas: {
            sellValue: obj[0].sellValue.toFixed(6).replace('.', ','),
            convertedSellValue: obj[0].convertedSellValue.toFixed(6).replace('.', ','),
            buyValue: obj[0].buyValue.toFixed(6).replace('.', ','),
            convertedBuyValue: obj[0].convertedBuyValue.toFixed(6).replace('.', ',')
        }
    }))
})

/* Returns an array grouped by currency */
const parsedTableListArrayGroup = computed(() => {
    const groupObject = props.converterTableList.reduce((acc, item) => {
        acc[item.currency] = acc[item.currency] ?? []
        acc[item.currency].push(item)

        return acc
    }, {})

    return Object.getOwnPropertyNames(groupObject).map(attributeName => groupObject[attributeName])
})

const tabsSegmentList = ref([{
    id: 'divisas',
    label: t('exchanges.table.titles.divisas'),
    selected: true
}, {
    id: 'notas',
    label: t('exchanges.table.titles.notas'),
    selected: false
}])

const tabsSegmentClickHandler = event => {
    tabsSegmentList.value.forEach(tab => {
        tab.selected = tab.id === event
    })
}

const showDivisas = computed(() => {
    return !getMobileAppState.value || (getMobileAppState.value && tabsSegmentList.value.find(obj => obj.selected).id === 'divisas')
})

const showNotas = computed(() => {
    return !getMobileAppState.value || (getMobileAppState.value && tabsSegmentList.value.find(obj => obj.selected).id === 'notas')
})
</script>

<style lang="scss" scoped>
.white-box-wrapper {
    @include cardGeneral1;
    @include commonEnteringAnimation;

    width: 100%;
    margin-top: 16px;
    padding: 16px;

    .converter-table {
        width: 100%;

        &.non-authenticated-feature {
            margin: 0 auto;
        }

        border: 1px solid var(--secondary-light-2);
        border-radius: 8px;

        .table-container {
            display: flex;
            flex-direction: column;

            width: 100%;

            position: relative;

            .table-headers {
                height: 48px;

                display: flex;
                flex-direction: row;
                align-items: center;

                @include H8-Medium;
                color: var(--secondary-dark-2);

                .header {
                    width: 100%;
                    padding: 0 16px;

                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    text-align: right;

                    &.currency-cell {
                        max-width: 25%;
                        padding-left: 16px;

                        flex-direction: row;
                        align-items: center;
                        flex: 0 0 25%;
                        gap: 16px;

                        text-align: left;
                    }
                }
            }

            .table-row {
                display: flex;
                flex-direction: row;
                align-items: center;

                height: 48px;

                font-size: 15px;
                font-weight: 500;

                color: var(--secondary-variant-1);

                &:nth-of-type(odd) {
                    background-color: var(--secondary-light-3);
                }

                .cell {
                    max-width: 100%;
                    padding-right: 16px;

                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 5px;
                    flex-basis: 0;
                    flex-grow: 1;

                    text-align: right;
                    color: var(--secondary-dark-2);

                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;

                    .converted-value {
                        @include H7-Regular;
                    }

                    .description {
                        @include H8-Regular;
                    }

                    &.currency-cell {
                        max-width: 25%;
                        padding-left: 16px;

                        flex-direction: row;
                        align-items: center;
                        flex: 0 0 25%;
                        gap: 16px;

                        text-align: left;

                        .currency-icon {
                            flex-shrink: 0;
                        }

                        .description-container {
                            display: flex;
                            flex-direction: column;
                            gap: 2px;

                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;

                            color: var(--secondary-dark-2);

                            .currency-text,
                            .currency-description {
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                            }

                            .currency-text {
                                @include H6-Medium;
                            }

                            .currency-description {
                                @include H8-Regular;
                            }
                        }
                    }
                }
            }
        }
    }

    &.mobile {
        padding: 16px 0 0 0;

        .last-update-date {
            width: calc(100% - 32px);
            margin: 0 16px 16px 16px;

            display: flex;
            align-items: center;

            @include H7-Regular;
            color: var(--secondary-dark-2);

            svg {
                margin-right: 8px;
            }
        }

        .tabs-segment-wrapper {
            width: calc(100% - 32px);
            margin: 0 16px 16px 16px;

            :deep(.tab) {
                width: 50%;
            }
        }

        .converter-table {
            border: unset;

            .table-container {
                .table-headers {
                    height: 32px;
                    padding-top: 16px;
                }

                .table-row {
                    height: 64px;
                    border-bottom: 1px solid var(--secondary-light-2);

                    &:nth-of-type(odd) {
                        background-color: transparent;
                    }

                    .currency-cell {
                        max-width: 55%;
                        flex: 0 0 55%;
                    }
                }
            }
        }
    }
}
</style>
